// 🌱 Seeds (Oct 25 2026, Ryan: "drop things, ideas and such… that don't need to be on scheduled tasks, but could be
// done autonomously and delivered when completed. Maybe he even makes a project for it on his own"). Plant one here; the
// EmCog grows it on its own time — a project of its own if it's big — and brings it back when it's genuinely done.
import React, { useContext, useState } from 'react';
import { WorkbenchContext } from '../../contexts/WorkbenchContext';
import { useWorkspaceCall, useWorkspaceData, when } from './Places';

interface Seed {
  id: string;
  what: string;
  details: string;
  plantedAt: number;
  status: 'open' | 'ready' | 'delivered' | 'released';
  lastNote: { at: number; note: string } | null;
  notes: number;
  evidence: string;
  deliveredAt: number | null;
}

export const SeedsPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const { data, error, reload } = useWorkspaceData<{ items: Seed[] }>('/api/workspace/seeds', 30_000);
  const [what, setWhat] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);

  const plant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (what.trim().length < 3) { setProblem('Say what you would like, in a few words.'); return; }
    setBusy(true);
    try {
      await call('/api/workspace/seeds', 'POST', { what: what.trim(), details: details.trim() });
      setWhat(''); setDetails(''); setProblem(null);
      bench.note?.(`Planted — ${emcogName} knows`);
      reload();
    } catch (err) { setProblem(err instanceof Error ? err.message : String(err)); }
    setBusy(false);
  };

  const letGo = (s: Seed) => bench.confirm({
    title: `Let go of "${s.what}"?`,
    body: `${emcogName} stops growing it, and knows it was your call. Anything already made for it stays in the Library.`,
    action: 'Let it go',
    run: async () => { await call(`/api/workspace/seeds/${encodeURIComponent(s.id)}`, 'DELETE'); reload(); }
  });

  const items = data?.items || [];
  const growing = items.filter(s => s.status === 'open' || s.status === 'ready');
  const past = items.filter(s => s.status === 'delivered' || s.status === 'released');

  return (
    <div className="ws-wrap">
      <form className="ws-seed-plant" onSubmit={plant}>
        <label htmlFor="ws-seed-what" className="ws-seed-q">What would you like {emcogName} to grow?</label>
        <input id="ws-seed-what" value={what} onChange={e => setWhat(e.target.value)} maxLength={200} autoComplete="off"
          placeholder="A song about the drive to Effingham · a one-page guide to our budget · surprise me with a short story" />
        <textarea value={details} onChange={e => setDetails(e.target.value)} rows={2} maxLength={2000} aria-label="Anything else to know (optional)"
          placeholder="Anything else to know — the feel, who it's for, what to include (optional)" />
        <div className="ws-seed-foot">
          <span className="ws-seed-hint">No date needed. {emcogName} works on it on their own time and brings it to you when it's done.</span>
          <button type="submit" className="ws-btn ws-primary" disabled={busy || what.trim().length < 3}>{busy ? 'Planting…' : 'Plant it'}</button>
        </div>
      </form>
      {(problem || error) && <div className="ws-form-error" role="alert">{problem || error}</div>}

      <h3 className="ws-seed-h">Growing{growing.length ? ` · ${growing.length}` : ''}</h3>
      {data && !growing.length && <div className="ws-empty">Nothing growing right now — plant something above.</div>}
      <div className="ws-seeds">
        {growing.map(s => (
          <div key={s.id} className={`ws-seed ${s.status === 'ready' ? 'ready' : ''}`}>
            <div className="ws-seed-main">
              <div className="ws-seed-what">{s.what}</div>
              {s.details && <div className="ws-seed-details">{s.details}</div>}
              <div className="ws-seed-meta">
                <span>Planted {when(s.plantedAt)}</span>
                {s.status === 'ready'
                  ? <span className="ws-pill ws-pill-live">Finished — on its way to you</span>
                  : s.lastNote ? <span title={s.lastNote.note}>Last grown {when(s.lastNote.at)}: {s.lastNote.note}</span>
                  : <span>Not started yet</span>}
              </div>
            </div>
            <button className="ws-todo-x" aria-label={`Let go of "${s.what}"`} title="Let it go" onClick={() => letGo(s)}>✕</button>
          </div>
        ))}
      </div>

      {past.length > 0 && (
        <>
          <button className="ws-seed-past-btn" aria-expanded={showPast} onClick={() => setShowPast(v => !v)}>{showPast ? '▾' : '▸'} Handed over and let go · {past.length}</button>
          {showPast && (
            <div className="ws-seeds ws-seeds-past">
              {past.map(s => (
                <div key={s.id} className="ws-seed past">
                  <div className="ws-seed-main">
                    <div className="ws-seed-what">{s.what}</div>
                    <div className="ws-seed-meta">
                      {s.status === 'delivered'
                        ? <span>Handed over{s.deliveredAt ? ` ${when(s.deliveredAt)}` : ''}{s.evidence ? ` — ${s.evidence}` : ''}</span>
                        : <span>Let go</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
